import React from "react";
import {
  Dialog,
  DialogProps,
  Box,
  Typography,
  Button,
  DialogContent,
} from "@mui/material";
import { useCurrentBrand } from "../../../hooks/useCurrentWorkspace";
import { terraCapacitorEnabled, useTerraSdk } from "../../../utils/terra-sdk";
import { useQuery } from "@tanstack/react-query";
import TerraService from "../../../services/TerraService";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import logo from "../../app/logo.png";

const CLIENT_TERRA_AUTH_TOKEN_QUERY_KEY = "client-terra-auth-token";
export const HIDE_RECONNECT_APPLE_HEALTH_DIALOG_KEY =
  "hide-reconnect-apple-health-dialog";

export interface ReconnectAppleHealthDialogProps extends DialogProps {
  force?: boolean;
}

export function ReconnectAppleHealthDialog(
  props: ReconnectAppleHealthDialogProps,
) {
  const { className, onClose, force, ...other } = props;
  const { brandName, brandedAppLogo, appName } = useCurrentBrand();

  const { data: terraOptions } = useQuery({
    queryKey: [CLIENT_TERRA_AUTH_TOKEN_QUERY_KEY],
    queryFn: () => TerraService.generateAuthToken(),
    enabled: true,
  });

  const { connected, init: initTerraSdk } = useTerraSdk(terraOptions);
  const [terraConnected] = useLocalStorage<boolean>("terra-connected", false);
  const [dismissed, setDismissed] = useLocalStorage<boolean>(
    HIDE_RECONNECT_APPLE_HEALTH_DIALOG_KEY,
    false,
  );

  const handleCloseButtonClick = React.useCallback(
    (event, reason?: "backdropClick" | "escapeKeyDown") => {
      if (onClose && !force) {
        onClose(event, reason || "backdropClick");
      }
    },
    [onClose, force],
  );

  const handleAuthorize = React.useCallback(async () => {
    await initTerraSdk();
    handleDismiss();
  }, [initTerraSdk]);

  const handleDismiss = React.useCallback(() => {
    setDismissed(true);
    if (onClose) {
      onClose({}, "escapeKeyDown");
    }
  }, [setDismissed, onClose]);

  // Clear dismissal when Apple Health is connected
  React.useEffect(() => {
    if (terraConnected && dismissed) {
      setDismissed(false);
    }
  }, [terraConnected, dismissed, setDismissed]);

  return dismissed ? null : (
    <Dialog
      onClose={handleCloseButtonClick}
      PaperProps={{ sx: { width: 1, maxWidth: 500 } }}
      componentsProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(3px)",
          },
        },
      }}
      {...other}
    >
      <DialogContent
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          textAlign: "center",
          padding: 3,
          paddingTop: 4,
        }}
      >
        <Box
          component={"img"}
          sx={{
            width: 80,
            height: 80,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: 2,
            borderRadius: "20%",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}
          src={brandedAppLogo || logo}
        ></Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 1,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Welcome to {brandName}!
          </Typography>
        </Box>
        <Typography variant="body1" gutterBottom>
          We noticed that you previously connected Apple Health to your account.
          Please re-authorize Apple Health to ensure you don’t lose any of your
          tracked data.
        </Typography>
        <Typography
          variant="caption"
          sx={{ mt: 1, mb: 2, color: "text.secondary" }}
        >
          You can also reconnect Apple Health later in Settings.
        </Typography>
        <Box sx={{ width: "100%", maxWidth: "300px", mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={connected || terraConnected}
            onClick={handleAuthorize}
          >
            {connected || terraConnected
              ? "Apple Health Authorized"
              : "Authorize Apple Health"}
          </Button>
          {!force && (
            <Button onClick={handleDismiss} variant="text" sx={{ mt: 2 }}>
              Not now
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
